// synced from loomweb blocks/remote-query-block-view.tsx (de-Tailwind'd).
/**
 * Remote query block (RemoteCMDRESULT). Faithful to loomweb's RemoteQueryBlockView:
 * optional associated text messages (rendered as markdown), then a result card
 * showing the query output (stdout then stderr) with a collapse toggle for long
 * output. loomweb's react-markdown is replaced by the host's ReplayMarkdown; the
 * Tailwind classes by namespaced `replay-*` CSS; lucide icons by the inline-SVG
 * set. Renders nothing when finished with no output and no text messages.
 */
import type { JSX } from "react";
import { useState } from "react";
import type { RemoteQueryBlock } from "@shared/loom-message-block";
import { LoaderIcon, ChevronDownIcon, ChevronRightIcon } from "./icons";
import { ReplayMarkdown } from "./replay-markdown";

/** Output collapse threshold (lines): more than this gets a toggle. */
const COLLAPSE_THRESHOLD = 3;

export function RemoteQueryBlockView({ block }: { block: RemoteQueryBlock }): JSX.Element | null {
  const [collapsed, setCollapsed] = useState<boolean>(block.collapsed ?? true);

  const isRunning = block.status === "in_progress" || block.status === "streaming";

  const totalLines =
    (block.output?.stdout.length ?? 0) + (block.output?.stderr.length ?? 0);
  const showCollapseToggle = totalLines > COLLAPSE_THRESHOLD;
  const hasTextMessages = block.textMessages !== undefined && block.textMessages.length > 0;

  // Nothing to show: finished, no output, no associated text.
  if (!isRunning && totalLines === 0 && !hasTextMessages) {
    return null;
  }

  const output = block.output;

  return (
    <div className="replay-remote-query-block">
      {hasTextMessages && (
        <div className="replay-remote-query-text">
          {block.textMessages!.map((text, i) => (
            <ReplayMarkdown key={`text-${i}`}>{text}</ReplayMarkdown>
          ))}
        </div>
      )}

      {(isRunning || output !== undefined) && (
        <div className="replay-block replay-remote-query-card">
          {isRunning && output === undefined && (
            <div className="replay-remote-running">
              <LoaderIcon className="replay-icon-xs replay-spin" />
              <span>等待结果...</span>
            </div>
          )}

          {output !== undefined && showCollapseToggle && (
            <div className="replay-remote-output-wrap">
              <button
                type="button"
                className="replay-trp-toggle"
                aria-expanded={!collapsed}
                onClick={() => setCollapsed((v) => !v)}
              >
                {collapsed ? (
                  <ChevronRightIcon className="replay-icon-xs" />
                ) : (
                  <ChevronDownIcon className="replay-icon-xs" />
                )}
                <span>{collapsed ? `展开 (${totalLines} 行)` : `收起 (${totalLines} 行)`}</span>
              </button>
              {!collapsed && (
                <div className="replay-remote-output replay-mono" role="region" aria-label="查询结果">
                  {output.stdout.map((line, i) => (
                    <div key={`out-${i}`} className="replay-remote-line">
                      {line}
                    </div>
                  ))}
                  {output.stderr.map((line, i) => (
                    <div key={`err-${i}`} className="replay-remote-line">
                      {line}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {output !== undefined && !showCollapseToggle && (
            <div className="replay-remote-output replay-mono" role="region" aria-label="查询结果">
              {output.stdout.map((line, i) => (
                <div key={`out-${i}`} className="replay-remote-line">
                  {line}
                </div>
              ))}
              {output.stderr.map((line, i) => (
                <div key={`err-${i}`} className="replay-remote-line">
                  {line}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
